import { useMemo } from 'react';
import { Autocomplete, AutocompleteItem } from '@heroui/react';
import { Paintbrush } from 'lucide-react';
import { useFinishes } from '@/api/finishes';
import type { SeatCushionSlot, MaterialLibrary, Finish } from '@estimator/shared';

interface SeatCushionFinishSummaryProps {
  slots: SeatCushionSlot[];
  materials: MaterialLibrary[];
  onUpdateSlot: (slotId: string, updates: Partial<SeatCushionSlot>) => void;
}

export default function SeatCushionFinishSummary({
  slots,
  materials,
  onUpdateSlot,
}: SeatCushionFinishSummaryProps) {
  const { data: finishes = [] } = useFinishes();
  const activeFinishes = useMemo(() => finishes.filter((f: Finish) => f.active), [finishes]);

  const slotsWithFinishNeeds = useMemo(() => {
    return slots
      .map((slot) => {
        if (!slot.material_id) return null;
        const mat = materials.find((m) => m.id === slot.material_id);
        if (!mat) return null;
        const needsSide1 = !!(mat as any).side1_requires_finish;
        const needsSide2 = !!(mat as any).side2_requires_finish;
        if (!needsSide1 && !needsSide2) return null;
        return { slot, mat, needsSide1, needsSide2 };
      })
      .filter(Boolean) as {
      slot: SeatCushionSlot;
      mat: MaterialLibrary;
      needsSide1: boolean;
      needsSide2: boolean;
    }[];
  }, [slots, materials]);

  if (slotsWithFinishNeeds.length === 0) return null;

  return (
    <div className="space-y-3">
      <h3
        className="text-xs font-semibold flex items-center gap-1.5"
        style={{ color: '#e67e22' }}
      >
        <Paintbrush size={12} /> Finish Assignments
      </h3>

      <div className="space-y-2">
        {slotsWithFinishNeeds.map(({ slot, mat, needsSide1, needsSide2 }) => (
          <div
            key={slot.id}
            className="rounded-lg p-3"
            style={{
              backgroundColor: 'var(--bg-panel)',
              border: '1px solid var(--border)',
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <div
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: slot.color }}
              />
              <span
                className="text-xs font-medium"
                style={{ color: 'var(--text-primary)' }}
              >
                {slot.name}
              </span>
              <span
                className="text-[10px]"
                style={{ color: 'var(--text-muted)' }}
              >
                -- {mat.name}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {needsSide1 && (
                <Autocomplete
                  label="Side 1 (Front) Finish"
                  selectedKey={slot.finish_id_side1 ?? undefined}
                  onSelectionChange={(key) => onUpdateSlot(slot.id, { finish_id_side1: (key as string) || null })}
                  size="sm"
                  placeholder="Select finish..."
                  inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-surface)]' } }}
                >
                  {activeFinishes.map((f: Finish) => (
                    <AutocompleteItem key={f.id}>
                      {f.name}
                      {f.finish_type ? ` (${f.finish_type})` : ''}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              )}
              {needsSide2 && (
                <Autocomplete
                  label="Side 2 (Back) Finish"
                  selectedKey={slot.finish_id_side2 ?? undefined}
                  onSelectionChange={(key) => onUpdateSlot(slot.id, { finish_id_side2: (key as string) || null })}
                  size="sm"
                  placeholder="Select finish..."
                  inputProps={{ classNames: { inputWrapper: 'bg-[var(--bg-surface)]' } }}
                >
                  {activeFinishes.map((f: Finish) => (
                    <AutocompleteItem key={f.id}>
                      {f.name}
                      {f.finish_type ? ` (${f.finish_type})` : ''}
                    </AutocompleteItem>
                  ))}
                </Autocomplete>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
